/**
 * Auth Utility Functions
 * 
 * Helper functions for extracting user identification from authentication tokens
 */

import { isDevelopmentMode } from '@/lib/utils';
import { decodeJWT } from '@/features/wellness/utils/jwtUtils';
import { getIdToken } from '@/features/wellness/services/wellnessAuth';
import { setSentryUser } from '@/utils/sentry';
import { identify as mixpanelIdentify } from '@/utils/mixpanel';

/**
 * Extract Cognito sub from JWT idToken
 * @returns Cognito sub string or null if token is missing or invalid
 */
export const getCognitoSub = (): string | null => {
  const idToken = getIdToken();
  if (!idToken) {
    return null;
  }

  const payload = decodeJWT(idToken);
  if (!payload || !payload.sub) {
    return null;
  }

  return payload.sub as string;
};

/**
 * Extract Cognito sub directly from a token string
 * @param token - JWT token string
 * @returns Cognito sub string or null if token is invalid
 */
export const extractCognitoSubFromToken = (token: string): string | null => {
  const payload = decodeJWT(token);
  if (!payload || !payload.sub) {
    return null;
  }
  return payload.sub as string;
};

/**
 * Identify user in both Sentry and Mixpanel
 * Unified function to keep both services in sync
 */
export const identifyUser = (
  userId: string,
  options?: {
    // Mixpanel-specific properties
    mixpanelProperties?: Record<string, unknown>;
  }
): void => {
  // Identify in Sentry (only user ID, no PII)
  setSentryUser(userId);

  // Identify in Mixpanel with optional properties
  mixpanelIdentify(userId, options?.mixpanelProperties);
};

/**
 * Get the Mixpanel user ID that should be used for identification
 * In development mode, uses email for easier debugging
 * In production, use Cognito sub for privacy
 * 
 * @param cognitoSub - The Cognito sub from the JWT token
 * @param userEmail - Optional user email
 * @returns The user ID to use for Mixpanel
 */
export const getMixpanelUserId = (
  cognitoSub: string,
  userEmail?: string
): string => {
  const isDevelopment = isDevelopmentMode();
  return isDevelopment && userEmail ? userEmail : cognitoSub;
};

/**
 * Get the current Mixpanel user ID for identification
 * Extracts Cognito sub from token and applies the same logic as identifyWellnessUser
 * In development mode, uses email for easier debugging
 * In production, uses Cognito sub for privacy
 * 
 * @param userEmail - Optional user email (if not provided, will use Cognito sub only)
 * @returns The user ID to use for Mixpanel, or null if Cognito sub cannot be extracted
 */
export const getCurrentMixpanelUserId = (userEmail?: string): string | null => {
  const cognitoSub = getCognitoSub();
  if (!cognitoSub) {
    return null;
  }
  return getMixpanelUserId(cognitoSub, userEmail);
};

/**
 * Identify wellness user (Cognito-based)
 * Extracts sub from token and identifies in both Sentry and Mixpanel
 * In development mode, uses email instead of Cognito sub for easier debugging
 */
export const identifyWellnessUser = (
  idToken: string,
  user: {
    email?: string;
    b2c?: boolean;
    needsProfile?: boolean;
  }
): void => {
  const cognitoSub = extractCognitoSubFromToken(idToken);
  if (!cognitoSub) {
    console.warn('[AuthUtils] Failed to extract Cognito sub from token');
    return;
  }

  const userId = getMixpanelUserId(cognitoSub, user.email);

  identifyUser(userId, {
    mixpanelProperties: {
      user_type: 'wellness',
      b2c: user.b2c ?? false,
      has_profile: !user.needsProfile,
      needs_profile: user.needsProfile ?? false,
    },
  });
};
